import { getAllCenters, isValidCenterCode } from '../../../center/centerCache'

/**
 * Validates a center code and returns a validated code with optional warning message.
 * Pre-fetches and caches all centers for validation and downstream use.
 *
 * @param {string} centerCode - The center code to validate (lowercase)
 * @returns {Promise<{centerCode: string, warning: string|null}>} Validated center code and warning
 *
 * @example
 * const { centerCode, warning } = await validateCenterCode('fhl')
 * // Returns: { centerCode: 'fhl', warning: null }
 *
 * @example
 * const { centerCode, warning } = await validateCenterCode('invalid')
 * // Returns: { centerCode: 'other', warning: 'Invalid center code...' }
 */
export default async function validateCenterCode(centerCode) {
  // Pre-fetch and cache all centers for validation and downstream use
  await getAllCenters()

  // Default to 'other' if no code provided
  if (!centerCode) {
    return { centerCode: 'other', warning: null }
  }

  // 'other' is always valid
  if (centerCode === 'other') {
    return { centerCode, warning: null }
  }

  // Validate against cached centers
  const isValid = await isValidCenterCode(centerCode)

  if (isValid) {
    return { centerCode, warning: null }
  }

  // Invalid center code - return 'other' with warning
  const warning = `Invalid center code "${centerCode}" provided. Defaulting to "other".`
  // eslint-disable-next-line no-console
  console.warn(warning)
  return { centerCode: 'other', warning }
}
